import { useState, useEffect } from "react";
import api, { formatErr } from "../../../lib/api";
import { card, btn, btnGhost, label, inputStyle } from "../roads/_roadsCommon";
import MicButton from "../../MicButton";

/** Ch 30 — AI Command Bar for Infrastructure / Real Estate. */
export default function AIDomainPanel({ projectId, domain = "re" }) {
  const [cmd, setCmd] = useState("");
  const [busy, setBusy] = useState(false);
  const [response, setResponse] = useState(null);
  const [history, setHistory] = useState([]);
  const [err, setErr] = useState("");

  const samples = domain === "infra" ? [
    "shift alignment 50m south between km 12 and km 15 to avoid the village",
    "design a 3-span prestressed concrete bridge at the river crossing km 18+200",
    "size all culverts for 25-year return period flood",
    "optimize the vertical profile to balance cut and fill between km 0 and km 10",
  ] : [
    "create a typical floor plan with 4 units of 2BHK (950 sqft) per floor",
    "place columns at all grid intersections with minimum 300x300mm",
    "route the HVAC duct from AHU-01 to all diffusers on the 5th floor",
    "calculate the total carpet area per RERA for all 2BHK units",
    "check NBC compliance for the building",
    "run clash detection between structural and MEP on 5th floor",
  ];

  const reload = async () => {
    try {
      const r = await api.get(`/projects/${projectId}/ai-domain/${domain}/history`);
      setHistory(r.data.commands || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [projectId, domain]);

  const run = async (cmdOverride) => {
    const used = (cmdOverride ?? cmd).trim();
    if (!used) return;
    setBusy(true); setErr(""); setResponse(null);
    try {
      const r = await api.post(`/projects/${projectId}/ai-domain/${domain}/command`,
        { command: used });
      setResponse(r.data.response);
      reload();
    } catch (e) { setErr(formatErr(e?.response?.data?.detail) || String(e)); }
    setBusy(false);
  };

  return (
    <div data-testid={`ai-domain-${domain}`} style={{ display: "grid",
            gridTemplateColumns: "320px 1fr", gap: 12, padding: 16,
            height: "calc(100vh - 280px)", minHeight: 700 }}>
      {/* Sample commands + history */}
      <div style={{ display: "grid", gridTemplateRows: "auto 1fr", gap: 12, minHeight: 0 }}>
        <div style={{ ...card, padding: 12 }}>
          <div style={label}>{domain === "infra" ? "Infrastructure" : "Real Estate"} Sample Commands</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
            {samples.map((s, i) => (
              <button key={i} data-testid={`ai-sample-${i}`}
                  style={{ ...btnGhost, textAlign: "left", padding: "5px 8px",
                              fontSize: 10, lineHeight: 1.3 }}
                  onClick={() => setCmd(s)}>{s}</button>
            ))}
          </div>
        </div>
        <div style={{ ...card, padding: 12, overflowY: "auto" }}>
          <div style={label}>Command History ({history.length})</div>
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
            {history.slice(0, 20).map(h => (
              <div key={h.id} style={{ fontSize: 10, padding: 6,
                                                background: "var(--pf-bg)", borderRadius: 4,
                                                border: "1px solid var(--pf-border)" }}>
                <div style={{ color: "#F26B1F", fontWeight: 600 }}>{h.command}</div>
                <div style={{ color: "var(--pf-muted)", marginTop: 2 }}>
                  → {h.response?.intent || "—"}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Command + response */}
      <div style={{ display: "grid", gridTemplateRows: "auto 1fr", gap: 12, minHeight: 0 }}>
        <div style={{ ...card, padding: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between",
                              alignItems: "center" }}>
            <div style={label}>Command Bar — Capexara Process Industry AI</div>
            <MicButton testId="ai-cmd-mic" size="sm"
                onTranscript={(t) => setCmd(prev => prev ? prev + " " + t : t)}
                onAutoSubmit={(t) => { setCmd(t); run(t); }} />
          </div>
          <textarea data-testid="ai-cmd-input"
              style={{ ...inputStyle, marginTop: 8, minHeight: 80,
                          fontSize: 12, fontFamily: "inherit" }}
              placeholder={`Type or speak a ${domain === "infra" ? "highway/bridge/drainage" : "floor plan/structural/MEP"} command (hold Space to record)…`}
              value={cmd}
              onChange={(e) => setCmd(e.target.value)} />
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button data-testid="ai-cmd-run" style={btn}
                onClick={run} disabled={busy || !cmd.trim()}>
              {busy ? "Thinking…" : "Run"}
            </button>
            <button style={btnGhost} onClick={() => setCmd("")}>Clear</button>
          </div>
        </div>
        <div style={{ ...card, padding: 14, overflowY: "auto" }}>
          <div style={label}>Response</div>
          {!response && !busy && (
            <div style={{ marginTop: 12, color: "var(--pf-muted)", fontSize: 11 }}>
              The AI will return a structured JSON: interpretation, intent,
              ops[], answer, warnings, and applicable standards (IRC / IS / NBC / RERA / IGBC).
            </div>
          )}
          {response && (
            <div style={{ marginTop: 8, fontSize: 11, lineHeight: 1.6 }}>
              <div><b>Intent:</b> <span style={{ color: "#F26B1F" }}>{response.intent}</span></div>
              <div style={{ marginTop: 6 }}><b>Interpretation:</b> {response.interpretation}</div>
              <div style={{ marginTop: 6 }}><b>Answer:</b> {response.answer}</div>
              {response.ops?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <b>Operations:</b>
                  <pre style={{ background: "var(--pf-bg)", padding: 8, fontSize: 10,
                                       borderRadius: 4, overflow: "auto" }}>
                    {JSON.stringify(response.ops, null, 2)}
                  </pre>
                </div>
              )}
              {response.standards?.length > 0 && (
                <div style={{ marginTop: 6, color: "#22c55e" }}>
                  ✓ Standards: {response.standards.join(", ")}
                </div>
              )}
              {response.warnings?.length > 0 && (
                <div style={{ marginTop: 6, color: "#f59e0b" }}>
                  ⚠ {response.warnings.join(" · ")}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {err && <div style={{ position: "absolute", bottom: 8, color: "#FF8888" }}>{err}</div>}
    </div>
  );
}
